import { useEffect, useMemo, useState } from 'react';
import { Alert, Spin, Typography } from 'antd';
import { ReactComponent as EmptyChartIllustration } from '../../assets/empty-chart.svg';
import { Card, Tab, TabList, TabPanel, TabPanels, Tabs } from '../../design-system';
import {
  exportGhePage4Workbook,
  getGhePage4,
  recalculateGhePage4Summary
} from '../../api/data';
import { GhePage4Payload, GhePage4SummaryCurves, TargetRockPage4View } from '../../@types/ghePage4/types';
import { useSelected } from '../../providers/selectedContext';
import { GhePage4ExportButton } from './GhePage4ExportButton';
import { GheNormalizedCurvesPanel } from './GheNormalizedCurvesPanel';
import { GhePage4TopSummaryCharts } from './GhePage4TopSummaryCharts';
import { GheProcessSelectionPanel } from './GheProcessSelectionPanel';
import {
  applySelectionsToPayload,
  buildSelectionState,
  selectionKey,
  updateSelectionState
} from './selectionUtils';
import { RockId } from '../../@types/gheInputs/types';
import styles from '../gheShared/gheStickyHeader.module.css';
import layout from './GheSelectionExportTab.module.css';

const ROCK_IDS: RockId[] = [
  'GHE1',
  'GHE2',
  'GHE3',
  'GHE4',
  'GHE5',
  'GHE6',
  'GHE7',
  'GHE8',
  'GHE9',
  'GHE10'
];

export const GheSelectionExportTab = ({ actions }: { actions?: import('react').ReactNode }) => {
  const { selectedInfo } = useSelected();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [payload, setPayload] = useState<GhePage4Payload | null>(null);
  const [selectionState, setSelectionState] = useState<Record<string, string[]>>({});
  const [recalculatedSummary, setRecalculatedSummary] = useState<Record<string, GhePage4SummaryCurves>>({});
  const [error, setError] = useState<string | null>(null);
  const [activeRockId, setActiveRockId] = useState<string>('');

  useEffect(() => {
    const projectUid = selectedInfo.project?.uid;
    if (!projectUid) {
      setPayload(null);
      return;
    }

    let isMounted = true;
    const loadPage4 = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getGhePage4(projectUid);
        if (!isMounted) return;
        const normalizedResponse = response
          ? {
              ...response,
              rocks: Array.isArray(response.rocks)
                ? response.rocks
                : Array.isArray((response as GhePage4Payload & { target_rocks?: GhePage4Payload['rocks'] }).target_rocks)
                ? (response as GhePage4Payload & { target_rocks?: GhePage4Payload['rocks'] }).target_rocks || []
                : []
            }
          : null;
        setPayload(normalizedResponse);
        setSelectionState(buildSelectionState(normalizedResponse));
        setRecalculatedSummary({});
      } catch (_err) {
        if (!isMounted) return;
        setError('Unable to load Page 4 data. Verify previous GHE workflow pages first.');
        setPayload(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPage4();
    return () => {
      isMounted = false;
    };
  }, [selectedInfo.project?.uid]);

  const payloadWithSelections = useMemo(
    () => applySelectionsToPayload(payload, selectionState),
    [payload, selectionState]
  );

  useEffect(() => {
    const projectUid = selectedInfo.project?.uid;
    if (!projectUid || Object.keys(selectionState).length === 0) return;

    const selections = (payloadWithSelections?.rocks || [])
      .filter(rock => rock.availability !== 'unavailable')
      .flatMap(rock =>
        rock.process_views.map(view => ({
          rock_id: rock.rock_id,
          process: view.process,
          selected_analogue_ids: selectionState[selectionKey(rock.rock_id, view.process)] ?? view.selected_analogues
        }))
      );

    const timer = setTimeout(async () => {
      try {
        const result = await recalculateGhePage4Summary(projectUid, selections);
        setRecalculatedSummary(result);
      } catch {
        // silent — keep showing previous summary
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [selectionState, selectedInfo.project?.uid]);

  const availableRocks = useMemo(
    () => (payloadWithSelections?.rocks || []).filter(rock => rock.availability !== 'unavailable'),
    [payloadWithSelections]
  );
  const summaryRocks = useMemo(() => {
    const rocks = (payload?.rocks || []).filter(rock => rock.availability !== 'unavailable');
    if (Object.keys(recalculatedSummary).length === 0) return rocks;
    return rocks.map(rock => {
      const updated = recalculatedSummary[rock.rock_id];
      return updated ? { ...rock, summary_curves: updated } : rock;
    });
  }, [payload, recalculatedSummary]);
  const activeRock =
    (payloadWithSelections?.rocks || []).find(rock => rock.rock_id === activeRockId) ?? null;

  useEffect(() => {
    if (ROCK_IDS.length === 0) {
      setActiveRockId('');
      return;
    }

    const hasActiveRock = ROCK_IDS.includes(activeRockId as (typeof ROCK_IDS)[number]);
    if (!hasActiveRock) {
      setActiveRockId(ROCK_IDS[0]);
    }
  }, [activeRockId]);

  const buildExportSelections = () =>
    availableRocks.flatMap(rock =>
      rock.process_views.map(view => ({
        rock_id: rock.rock_id,
        process: view.process,
        selected_analogue_ids:
          selectionState[selectionKey(rock.rock_id, view.process)] || []
      }))
    );

  const handleExport = async () => {
    const projectUid = selectedInfo.project?.uid;
    if (!projectUid) return;

    try {
      setExporting(true);
      const content = await exportGhePage4Workbook(projectUid, {
        project_uid: projectUid,
        selections: buildExportSelections()
      });
      const blob = content instanceof Blob ? content : new Blob([content]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ghe-page4-results-${projectUid}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (_err) {
      setError('Unable to export Page 4 workbook.');
    } finally {
      setExporting(false);
    }
  };

  const renderRockCard = (rock: TargetRockPage4View) => (
    <div key={rock.rock_id} data-testid={`ghe-page4-rock-${rock.rock_id}`}>
      <GheNormalizedCurvesPanel
        rock={rock}
        renderSelection={process => {
          const view = rock.process_views.find(item => item.process === process);
          return (
            <GheProcessSelectionPanel
              process={process}
              options={view?.selectable_analogues || []}
              value={selectionState[selectionKey(rock.rock_id, process)] || view?.selected_analogues || []}
              onChange={selected =>
                setSelectionState(prev =>
                  updateSelectionState(prev, rock.rock_id, process, selected)
                )
              }
            />
          );
        }}
      />
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '1rem' }}>
        <Spin data-testid="ghe-page4-loading" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        data-testid="ghe-page4-error"
      />
    );
  }

  if (!payloadWithSelections || availableRocks.length === 0) {
    return (
      <div
        data-testid="ghe-page4-empty"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: '48px 32px',
        }}
      >
        <EmptyChartIllustration style={{ width: 320 }} />
        <p style={{ margin: 0, fontSize: 'var(--ds-font-size-lg)', fontWeight: 500, color: 'var(--ds-emerald-zinc-700)' }}>
          No selection data yet
        </p>
        <p style={{ margin: 0, fontSize: 'var(--ds-font-size-md)', color: 'var(--ds-emerald-zinc-400)', textAlign: 'center', maxWidth: 340, lineHeight: 1.5 }}>
          Save GHE inputs on "GHE Inputs" page to see the selection data.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="ghe-page4-tab" className={layout.root}>
      <div className={styles.stickyHeader}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Krel and Pc Analogue Selection
        </Typography.Title>
        <div className={styles.headerActions}>
          <GhePage4ExportButton
            disabled={availableRocks.length === 0}
            loading={exporting}
            onExport={handleExport}
          />
          {actions}
        </div>
      </div>
      <div className={layout.split}>
        <div className={layout.summaryPane}>
          <GhePage4TopSummaryCharts rocks={summaryRocks} />
        </div>
        <div className={layout.mainPane}>
          {activeRockId ? (
            <Card fullWidth padding="md" className={layout.mainCard}>
              <Tabs value={activeRockId} onChange={setActiveRockId} className={layout.mainTabs}>
                <TabList>
                  {ROCK_IDS.map(rockId => (
                    <Tab key={rockId} value={rockId}>
                      {rockId}
                    </Tab>
                  ))}
                </TabList>
                <TabPanels className={layout.mainPanels}>
                  {ROCK_IDS.map(rockId => {
                    const rock = (payloadWithSelections?.rocks || []).find(item => item.rock_id === rockId);

                    return (
                      <TabPanel key={rockId} value={rockId}>
                        {rock && rock.availability !== 'unavailable' ? (
                          renderRockCard(rock)
                        ) : (
                          <Alert
                            type="info"
                            showIcon
                            message={`${rockId} analogue data unavailable for current workflow inputs.`}
                            data-testid={`ghe-page4-unavailable-${rockId}`}
                          />
                        )}
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
};
